/** The template queue as one table at the top of /choose (board group `template_queue`): every clip that needs a
 * template with its data, sortable and searchable, so clips are not only gone through one by one. A row click (or
 * its title link) opens the clip in the chooser below (`?clip=<id>`). Row checkboxes + the bulk bar choose one
 * template for many clips: one `POST /api/clips/{id}/renders` per clip, in order, each with its own seed. */
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, newIdempotencyKey, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { BoardTable, type SortState } from "../../ws7/board/BoardTable";
import { errorText } from "../common";
import type { BoardPage, TemplateOut } from "../types";
import { chooseTemplates } from "./api";
import { FOOTAGE } from "./machine";

export const QUEUE_GROUP = "template_queue";
const OPEN_KEY = "vp.choose.queueTable";

export type BulkResult =
  | { kind: "queued"; renderIds: number[] }
  | { kind: "refused"; message: string }
  | { kind: "stopped" }
  | { kind: "pending" };

function readOpen(): boolean {
  try {
    return globalThis.localStorage?.getItem(OPEN_KEY) !== "closed";
  } catch {
    return true;
  }
}

function writeOpen(open: boolean): void {
  try {
    globalThis.localStorage?.setItem(OPEN_KEY, open ? "open" : "closed");
  } catch {
    // storage blocked: the panel still toggles for this page
  }
}

/** Why a template cannot be chosen at all (the catalog part of the cards' disabled reason). */
export function templateProblem(t: TemplateOut): string | null {
  if (t.disabled_reason) return t.disabled_reason;
  if (!t.available) return "template unavailable";
  if (!t.linux_certified) return "not certified on Linux";
  return null;
}

/** One clip, one template: a footage template on a clip another footage template already owns is refused. */
export function ownerConflict(templateId: string, owner: unknown): string | null {
  if (typeof owner !== "string" || !owner || !FOOTAGE.has(templateId) || !FOOTAGE.has(owner) || owner === templateId) {
    return null;
  }
  return `one clip, one template: already rendered as ${owner}`;
}

export function boardKey(params: Record<string, unknown>) {
  return qk.board(QUEUE_GROUP, params);
}

export function QueueTable({ current, onPin, onChanged }: {
  current: number | null;
  onPin: (clipId: number) => void;
  /** After a bulk run: the chooser re-reads its queue. */
  onChanged?: () => void;
}) {
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [open, setOpenState] = useState(readOpen);
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortState>({ desc: true });
  const [selected, setSelected] = useState<number[]>([]);
  const [tid, setTid] = useState("");
  const [results, setResults] = useState<Record<number, BulkResult>>({});
  const [running, setRunning] = useState(false);
  const [stopped, setStopped] = useState<string | null>(null);

  const params = { q: q || undefined, sort: sort.sort, desc: sort.desc };
  const page = useQuery({
    queryKey: boardKey(params),
    enabled: open,
    placeholderData: keepPreviousData,
    queryFn: () => unwrap(api.GET("/api/board/{group}", {
      params: { path: { group: QUEUE_GROUP }, query: { q: q || undefined, sort: sort.sort, desc: sort.desc, limit: 2000 } },
    })),
  });
  const catalog = useQuery({
    queryKey: qk.list("templates"),
    enabled: open,
    staleTime: 60_000,
    queryFn: () => unwrap(api.GET("/api/templates")),
  });

  const setOpen = (v: boolean) => {
    setOpenState(v);
    writeOpen(v);
  };
  const data: BoardPage | undefined = page.data;
  const items = data?.items ?? [];
  const visible = new Set(items.map((it) => it.id));
  const sel = selected.filter((id) => visible.has(id));
  const toggle = (id: number) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(sel.length === items.length ? [] : items.map((it) => it.id));
  const onSort = (key: string) => setSort((s) => (s.sort === key ? { sort: key, desc: !s.desc } : { sort: key, desc: true }));
  const search = (e: FormEvent) => {
    e.preventDefault();
    setQ(draft.trim());
  };

  const templates = catalog.data ?? [];
  const chosen = templates.find((t) => t.id === tid);
  const chosenProblem = chosen ? templateProblem(chosen) : null;

  const runBulk = async () => {
    if (running || !chosen || chosenProblem || sel.length === 0) return;
    setRunning(true);
    setStopped(null);
    const order = items.map((it) => it.id).filter((id) => sel.includes(id)); // the table's order
    const owners = new Map(items.map((it) => [it.id, it.cells.owner?.value]));
    const out: Record<number, BulkResult> = Object.fromEntries(order.map((id) => [id, { kind: "pending" } as BulkResult]));
    setResults({ ...out });
    let queued = 0;
    let refused = 0;
    let halt: string | null = null;
    for (const id of order) {
      if (halt) {
        out[id] = { kind: "stopped" };
        continue;
      }
      const conflict = ownerConflict(chosen.id, owners.get(id));
      if (conflict) {
        out[id] = { kind: "refused", message: conflict };
        refused++;
        setResults({ ...out });
        continue;
      }
      try {
        const renders = await chooseTemplates(id, [chosen.id], newIdempotencyKey());
        out[id] = { kind: "queued", renderIds: renders.map((r) => r.id) };
        queued++;
      } catch (e) {
        const msg = errorText(e);
        out[id] = { kind: "refused", message: msg };
        refused++;
        // the render queue is full (render.max_queued), a daily quota, or someone else holds the clip: stop here
        if (e instanceof ApiError && (e.status === 409 || e.status === 429)) halt = msg;
      }
      setResults({ ...out });
    }
    setResults({ ...out });
    setRunning(false);
    setStopped(halt);
    setSelected((s) => s.filter((id) => out[id]?.kind !== "queued"));
    announce(`${chosen.title}: ${queued} render${queued === 1 ? "" : "s"} queued, ${refused} refused`
      + (halt ? `; stopped: ${halt}` : ""), halt || refused ? "error" : undefined);
    void qc.invalidateQueries({ queryKey: ["board", QUEUE_GROUP] });
    void qc.invalidateQueries({ queryKey: ["list", "work-next"] });
    onChanged?.();
  };

  /** One clip's outcome; `testId` differs between the results list (stays after the refresh) and the row. */
  const resultView = (id: number, testId: string) => {
    const r = results[id];
    if (!r) return null;
    switch (r.kind) {
      case "pending":
        return <span className="ws8-muted" data-testid={testId}>…</span>;
      case "stopped":
        return <span className="ws8-muted" data-testid={testId}>not sent (stopped)</span>;
      case "queued":
        return (
          <span data-testid={testId}>
            {r.renderIds.map((rid, i) => (
              <span key={rid}>{i > 0 ? ", " : ""}<Link to={`/renders/${rid}`}>render #{rid}</Link></span>
            ))}
          </span>
        );
      case "refused":
        return <span className="ws7-tone-bad" data-testid={testId}>{r.message}</span>;
    }
  };
  const resultIds = Object.keys(results).map(Number);

  return (
    <section className="ws8-panel" data-testid="queue-table" aria-labelledby="queue-table-h">
      <div className="ws8-row">
        <h2 className="ws8-h2" id="queue-table-h">
          Clips that need a template{data ? ` (${data.total})` : ""}
        </h2>
        <button type="button" data-testid="queue-table-toggle" aria-expanded={open} aria-controls="queue-table-body"
          onClick={() => setOpen(!open)}>
          {open ? "Hide table" : "Show table"}
        </button>
      </div>
      {open && (
        <div id="queue-table-body">
          {data?.help && <p className="ws8-muted">{data.help}</p>}
          <form className="ws8-row" role="search" aria-label="Search the queue" onSubmit={search}>
            <label className="ws8-inline">
              Search{" "}
              <input type="search" value={draft} data-testid="queue-search" placeholder="artist, song, MV, #id"
                onChange={(e) => setDraft(e.target.value)} />
            </label>
            <button type="submit" data-testid="queue-search-submit">Search</button>
            {q && <button type="button" onClick={() => { setDraft(""); setQ(""); }}>Clear</button>}
          </form>
          <div className="ws8-row" role="group" aria-label="Choose one template for the selected clips"
            data-testid="queue-bulk">
            <span data-testid="queue-selected">{sel.length} selected</span>
            <label className="ws8-inline">
              Template{" "}
              <select data-testid="queue-bulk-template" value={tid} onChange={(e) => setTid(e.target.value)}>
                <option value="">Choose a template…</option>
                {templates.map((t) => {
                  const why = templateProblem(t);
                  return (
                    <option key={t.id} value={t.id} disabled={why != null}>
                      {t.title} ({t.content_class}){why ? ` — ${why}` : ""}
                    </option>
                  );
                })}
              </select>
            </label>
            <button type="button" className="ws8-primary" data-testid="queue-bulk-choose"
              disabled={running || !chosen || chosenProblem != null || sel.length === 0} onClick={() => void runBulk()}>
              {running ? "Choosing…" : `Choose for selected (${sel.length})`}
            </button>
            {chosen && FOOTAGE.has(chosen.id) && (
              <span className="ws8-muted">One clip, one template: clips another footage template owns are refused.</span>
            )}
          </div>
          {resultIds.length > 0 && (
            <ul className="ws8-list" aria-label="Results of Choose for selected" data-testid="queue-results">
              {resultIds.map((id) => (
                <li key={id}>
                  <Link to={`/clips/${id}`}>Clip #{id}</Link>: {resultView(id, `queue-result-${id}`)}
                </li>
              ))}
            </ul>
          )}
          {stopped && (
            <div role="alert" className="error-box" data-testid="queue-bulk-stopped">
              <strong>Stopped:</strong> {stopped}. The remaining clips were not sent.
            </div>
          )}
          {catalog.isError && (
            <p role="alert" className="error-box" data-testid="queue-templates-error">
              Templates could not be loaded: {errorText(catalog.error)}
            </p>
          )}
          {page.isPending && <p className="ws8-muted" role="status">Loading the queue…</p>}
          {page.isError && (
            <div role="alert" className="error-box" data-testid="queue-table-error">
              {errorText(page.error)}{" "}
              <button type="button" onClick={() => void page.refetch()}>Reload</button>
            </div>
          )}
          {data && items.length === 0 && <p data-testid="queue-table-empty">No clip needs a template{q ? " (search)" : ""}.</p>}
          {data && items.length > 0 && (
            <div className="ws7-scroll ws8-queue-scroll" role="region" aria-label="Template queue table" tabIndex={0}>
              <BoardTable page={data} group={QUEUE_GROUP} label="Clips that need a template" sort={sort} onSort={onSort}
                current={current} onRowClick={(it) => onPin(it.id)}
                select={{ ids: sel, toggle, toggleAll, testId: (id) => `queue-select-${id}` }}
                extra={resultIds.length ? { title: "Result", cell: (it) => resultView(it.id, `queue-row-result-${it.id}`) }
                  : undefined} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
